import BizGenerateButton from "@/components/bussiness/BizGenerateButton";
import { NSlider } from "@/components/NSlider";
import TextArea from "@/components/TextArea";
import { InfoText, LabelText } from "@/modules/editor/components/styleComponents/Texts";
import { uiStore } from "@/stores/ui";
import { userAssetsStore } from "@/stores/userAssets";
import events, { MessageType } from "@/utils/events";
import { useTranslate } from "@/utils/lang";
import { Editor_Words } from "@/utils/lang/editor";
import { useOwnToast, toast } from "@/utils/misc";
import { translateAndCheck } from "@/utils/prompt";
import { Box, Button, Center, chakra, Flex, Text, useBoolean } from "@chakra-ui/react";
import { langStore } from "@noli/business";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo } from "react";
import { Draft_Words } from "../lang";
import { draftStore } from "../store";
import { DraftTaskType } from "../types";
import { ModelSelector } from "./ModelSelector";
import ResolutionRadio from "./ResolutionRadio";
import { SizeRadio } from "./SizeRadio";
import { ReactComponent as robot } from "@/assets/icons/animate_robot.svg";
import Icon from "@/components/Icon";

function DraftPanel(props: { src?: string }) {
  const { src } = props;
  const { translate } = useTranslate();

  useEffect(() => {
    userAssetsStore.fetchHeartCoin();
  }, []);

  useEffect(() => {
    draftStore.setUrl(src ?? "");
    if (uiStore.leftPanel === "img2img") draftStore.setTaskType(DraftTaskType.img2img);
    if (uiStore.leftPanel === "txt2img") draftStore.setTaskType(DraftTaskType.txt2img);
  }, [uiStore.leftPanel, src]);

  const lang = langStore.tgt;
  const t = useOwnToast();
  const [isLoading, loadingOp] = useBoolean(false);
  async function onSubmit(): Promise<void> {
    // sanity check
    if (!draftStore.text) {
      toast(t, "warning", translate(Editor_Words["no-text-input-message"]));
      return;
    }
    if (draftStore.taskType === "img2img" && !draftStore.url) {
      toast(t, "warning", translate(Editor_Words["no-text-input-message"]));
      return;
    }
    loadingOp.on();
    if (!(await translateAndCheck(draftStore.text, t, lang, false, false))) {
      loadingOp.off();
      return;
    }
    loadingOp.off();
    await draftStore.postTask({
      t,
      lang,
    });
  }

  const disabled = useMemo(() => {
    if (draftStore.taskType === DraftTaskType.img2img) {
      return !src || isLoading;
    }
    return isLoading;
  }, [isLoading, draftStore.taskType, src]);

  return (
    <Box w="100%">
      <LabelText display="flex" justifyContent="space-between">
        <Text>{translate(Editor_Words["prompt-input-title"])}：</Text>
        <Text pos="relative" onClick={() => events.emit(MessageType.PROMPT_EXPAND)}>
          <Text
            border="1px solid"
            pos="relative"
            px="10px"
            bg="#323232"
            borderRadius="4px"
            color="#b3b3b3"
            cursor="pointer"
            zIndex={1}
            _hover={{ color: "white" }}
          >
            {translate(Editor_Words["prompt-expand-modal-title"])}
          </Text>
          <Text
            pos="absolute"
            px="6px"
            right={0}
            bottom="100%"
            bg="#36C19E"
            color="white"
            borderRadius="3px"
            fontSize="12px"
            _after={{
              content: "''",
              bg: "#36c19e",
              w: "10px",
              h: "10px",
              pos: "absolute",
              right: 0,
              top: "80%",
            }}
          >
            new
          </Text>
        </Text>
      </LabelText>
      <Box h="fit-content" pos="relative" my="16px">
        <TextArea
          w="100%"
          my="0px"
          lang={lang}
          textAreaH={300}
          minRows={5}
          maxRows={10}
          placeholderWord={Draft_Words["textarea-placeholder"]}
          text={draftStore.text}
          setText={draftStore.setText}
        />
        {draftStore.promptExpanding && (
          <Flex
            w="100%"
            h="100%"
            pos="absolute"
            top="0"
            left="0"
            flexGrow={1}
            bg="#323232"
            borderRadius="6px"
            border="1px solid white"
            justify="center"
            flexDir="column"
            align="center"
          >
            <Icon w="32px" h="32px" svg={robot} />
            <Text color="#b3b3b3">提示词生成中</Text>
          </Flex>
        )}
      </Box>

      <ModelSelector
        value={draftStore.modelTpe}
        setValue={draftStore.setModelType}
        title={translate(Draft_Words["draft-model-select"])}
      />
      <SizeRadio />
      <ResolutionRadio />
      {draftStore.taskType === DraftTaskType.img2img && (
        <NSlider
          mt="16px"
          value={draftStore.fidelity}
          max={1}
          min={0}
          step={0.1}
          label={translate(Editor_Words["fidelity"])}
          onChange={draftStore.setFidelity}
        />
      )}
      <BizGenerateButton
        mt="16px"
        cost={draftStore.cost * 2}
        onSubmit={onSubmit}
        showCost
        useCoin="payed"
        buttonProps={{
          isLoading,
          isDisabled: disabled,
        }}
      >
        {translate(Draft_Words["submit"])}
      </BizGenerateButton>
    </Box>
  );
}
export default observer(DraftPanel);
